/**
 * pf-cart.js
 *
 * AJAX cart drawer for the pf-1842 theme. Registers the
 * <pf-cart-drawer> custom element and exposes a global controller:
 *
 *   window.pfCart.open()                          // open the drawer
 *   window.pfCart.close()                         // close the drawer
 *   window.pfCart.refresh()                       // re-fetch + re-render
 *   window.pfCart.addToCart(variantId, qty, opts) // add and open drawer
 *
 * Cart state is owned by Shopify (server-rendered Liquid). After every
 * mutation we ask Shopify to re-render this section using the Sections
 * API with this section's unique ID (`section.id` from Liquid, e.g.
 * "header.pf-cart-drawer") so Shopify renders the merchant-customized
 * instance — not the section file's defaults. Per-line loaders fire
 * optimistically while a mutation is in flight; if the request fails
 * the line's loader clears.
 *
 * Loaded once from layout/theme.liquid with `defer`.
 */

(function () {
  if (customElements.get('pf-cart-drawer')) return;

  function fetchJson(url, opts) {
    return fetch(url, opts).then((res) => {
      if (!res.ok) {
        return res.json().catch(() => ({})).then((body) => {
          const err = new Error(body.description || body.message || 'Cart request failed');
          err.status = res.status;
          err.body = body;
          throw err;
        });
      }
      return res.json();
    });
  }

  // Collect section IDs for every cart surface currently in the DOM
  // (drawer + cart-page). Passing all of them in `sections=` on each
  // POST means Shopify's response includes a fresh render of each
  // instance, so we can update them all from one request without a
  // follow-up GET. New surfaces added later are picked up automatically
  // because this re-queries every time it's called.
  function getCartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('pf-cart-drawer, pf-cart-page').forEach((el) => {
      const id = el.dataset.sectionId;
      if (id) ids.add(id);
    });
    return [...ids].join(',');
  }

  class PfCartDrawer extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.abortController = new AbortController();

      this.refs = {
        backdrop: this.querySelector('[data-pf-cart-backdrop]'),
        panel: this.querySelector('.pf-cart-drawer__panel'),
        body: this.querySelector('[data-pf-cart-body]'),
      };

      this.bindStaticListeners();
      this.bindDynamicListeners();
      this.listenForCartUpdates();
      this.exposeGlobalApi();
      this.maybeLoadRecommendations();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.unlockScroll();
      if (window.pfCart && window.pfCart._owner === this) {
        delete window.pfCart;
      }
    }

    exposeGlobalApi() {
      const api = {
        _owner: this,
        open: () => this.open(),
        close: () => this.close(),
        refresh: () => this.refresh(),
        isOpen: () => this.classList.contains('is-open'),
        addToCart: (variantId, qty, opts) => this.addToCart(variantId, qty, opts),
      };
      window.pfCart = api;
    }

    // Listeners on elements that exist before and after re-render.
    bindStaticListeners() {
      const { signal } = this.abortController;

      this.refs.backdrop?.addEventListener('click', () => this.close(), { signal });
      document.addEventListener(
        'keydown',
        (e) => {
          if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
        },
        { signal }
      );

      // Delegate clicks for the dynamic close buttons (re-rendered with
      // each section refresh).
      this.addEventListener('click', (e) => {
        const closeEl = e.target.closest('[data-pf-cart-close]');
        if (closeEl) {
          // Anchors with data-pf-cart-close should still navigate; just
          // close the drawer.
          this.close();
        }
      }, { signal });
    }

    // Listeners on elements inside the dynamic body region. Re-attached
    // after every re-render. All scoped to the parent AbortController so
    // they're torn down on disconnect (re-renders create new nodes so
    // the old listeners die with them either way, but the signal keeps
    // memory accounting tidy).
    bindDynamicListeners() {
      const { signal } = this.abortController;
      this.querySelectorAll('[data-pf-qty-decrease]').forEach((btn) => {
        btn.addEventListener('click', () => this.changeQty(btn.dataset.lineKey, -1, btn), { signal });
      });
      this.querySelectorAll('[data-pf-qty-increase]').forEach((btn) => {
        btn.addEventListener('click', () => this.changeQty(btn.dataset.lineKey, +1, btn), { signal });
      });
      this.querySelectorAll('[data-pf-qty-input]').forEach((input) => {
        input.addEventListener('change', () => {
          const next = parseInt(input.value, 10);
          if (Number.isNaN(next) || next < 0) {
            input.value = input.defaultValue;
            return;
          }
          this.setQty(input.dataset.lineKey, next, input.closest('[data-pf-cart-line]'));
        }, { signal });
      });

      // Discount code submit — POSTs to /cart/update.js with
      // attributes[discount_code], then re-renders. Real validation
      // happens at checkout; we just store the code so the section can
      // surface it and the checkout link can append it as a URL param.
      const discountForm = this.querySelector('[data-pf-cart-discount-form]');
      if (discountForm) {
        discountForm.addEventListener('submit', (e) => {
          e.preventDefault();
          const input = discountForm.querySelector('[data-pf-cart-discount-input]');
          const code = (input?.value || '').trim();
          this.applyDiscount(code);
        }, { signal });
      }

      // Notes textarea — debounced save (1s after last keystroke) and an
      // explicit save on blur.
      const noteEl = this.querySelector('[data-pf-cart-note]');
      if (noteEl) {
        let noteTimer = null;
        const queueSave = () => {
          if (noteTimer) clearTimeout(noteTimer);
          noteTimer = setTimeout(() => this.saveNote(noteEl.value), 800);
        };
        noteEl.addEventListener('input', queueSave, { signal });
        noteEl.addEventListener('blur', () => {
          if (noteTimer) clearTimeout(noteTimer);
          this.saveNote(noteEl.value);
        }, { signal });
      }
    }

    // Cross-instance sync: any cart mutation in this controller fires
    // a `pf:cart:updated` event on `document`. Other cart controllers
    // (the cart page, another drawer instance) listen and consume
    // their own section HTML directly from `detail.sections` so the
    // UI stays consistent without each controller having to know
    // about the others. The `source` field lets a listener ignore
    // events it dispatched itself; `sourceName` is a human-readable
    // tag ('drawer' / 'page') for debugging or for external listeners
    // that want to react to one specific surface.
    dispatchCartUpdated(data) {
      document.dispatchEvent(new CustomEvent('pf:cart:updated', {
        detail: {
          source: this,
          sourceName: 'drawer',
          sectionId: this.sectionId,
          sections: (data && data.sections) || null,
          data: data || null,
        },
      }));
    }

    listenForCartUpdates() {
      document.addEventListener('pf:cart:updated', (e) => {
        if (!e.detail || e.detail.source === this) return;
        // If the user is mid-edit in this section (note textarea,
        // discount input, qty input), skip — replacing the markup
        // would clobber the unsaved typing.
        const active = document.activeElement;
        if (active && this.contains(active)) {
          const tag = active.tagName;
          if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        }
        // Preferred path: consume our section HTML from the event's
        // sections payload (single request, no extra round-trip).
        const html = e.detail.sections && e.detail.sections[this.sectionId];
        if (html) {
          this.replaceSection(html);
          return;
        }
        // Fallback for older event sources that didn't include
        // sections in the payload.
        this.refresh();
      }, { signal: this.abortController.signal });
    }

    async applyDiscount(code) {
      const hint = this.querySelector('[data-pf-cart-discount-hint]');
      try {
        const data = await fetchJson('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            attributes: { discount_code: code },
            sections: getCartSectionIds(),
          }),
        });
        this.applyResponse(data);
        // Hint visibility is handled by section re-render; nothing to do
        // here unless the new section render didn't include the hint.
      } catch (err) {
        if (hint) {
          hint.hidden = false;
          hint.textContent = err.message || 'Could not save code.';
          hint.classList.remove('pf-cart-drawer__field-hint--ok');
        }
      }
    }

    async saveNote(value) {
      const status = this.querySelector('[data-pf-cart-note-status]');
      try {
        const data = await fetchJson('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ note: value, sections: getCartSectionIds() }),
        });
        if (status) {
          status.hidden = false;
          // Auto-hide after 2.5s so the cart isn't permanently noisy.
          clearTimeout(this._noteHideTimer);
          this._noteHideTimer = setTimeout(() => { status.hidden = true; }, 2500);
        }
        // Don't replace this drawer's own section — that would reset the
        // textarea cursor while the user might still be typing. Just
        // dispatch so other instances can pick up the new note from the
        // payload.
        this.dispatchCartUpdated(data);
      } catch (err) {
        console.warn('[pf-cart] save note failed', err);
      }
    }

    async changeQty(key, delta, originBtn) {
      const lineEl = originBtn.closest('[data-pf-cart-line]');
      if (!lineEl) return;
      const input = lineEl.querySelector('[data-pf-qty-input]');
      const current = parseInt(input?.value, 10) || 0;
      const next = Math.max(0, current + delta);
      await this.setQty(key, next, lineEl);
    }

    async setQty(key, quantity, lineEl) {
      if (!lineEl) return;
      lineEl.classList.add('is-loading');
      try {
        const data = await fetchJson('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: getCartSectionIds(),
          }),
        });
        this.applyResponse(data);
      } catch (err) {
        lineEl.classList.remove('is-loading');
        console.warn('[pf-cart] change failed', err);
      }
    }

    async addToCart(variantId, quantity, opts) {
      if (!variantId) return;
      const payload = {
        items: [{
          id: typeof variantId === 'string' ? parseInt(variantId, 10) : variantId,
          quantity: quantity || 1,
          properties: (opts && opts.properties) || undefined,
          selling_plan: (opts && opts.sellingPlan) || undefined,
        }],
        sections: getCartSectionIds(),
      };
      try {
        const data = await fetchJson('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload),
        });
        this.applyResponse(data);
        this.open();
        this.bumpHeaderBadge();
        return data;
      } catch (err) {
        console.warn('[pf-cart] add failed', err);
        if (opts && typeof opts.onError === 'function') opts.onError(err);
        throw err;
      }
    }

    async refresh() {
      try {
        const res = await fetch('/?sections=' + encodeURIComponent(this.sectionId), {
          headers: { 'Accept': 'application/json' },
        });
        if (!res.ok) throw new Error('Refresh failed');
        const data = await res.json();
        this.replaceSection(data[this.sectionId]);
      } catch (err) {
        console.warn('[pf-cart] refresh failed', err);
      }
    }

    // Shopify's response shape from cart endpoints with `sections`:
    //   { ...cart, sections: { 'pf-cart-drawer': '<html...>' } }
    applyResponse(data) {
      if (data && data.sections && data.sections[this.sectionId]) {
        this.replaceSection(data.sections[this.sectionId]);
      }
      this.syncHeaderBadge(data && typeof data.item_count === 'number' ? data.item_count : null);
      this.dispatchCartUpdated(data);
    }

    replaceSection(html) {
      if (!html) return;
      const temp = document.createElement('div');
      temp.innerHTML = html.trim();
      // Section render includes wrapping <pf-cart-drawer> markup. We want
      // the inner content — find the new pf-cart-drawer element and copy
      // its children + data attrs over to the live element.
      const newRoot = temp.querySelector('pf-cart-drawer');
      if (!newRoot) {
        // Fallback: replace inner HTML wholesale.
        this.innerHTML = temp.innerHTML;
      } else {
        // Sync data attrs (item-count, threshold).
        ['data-item-count', 'data-free-shipping-threshold'].forEach((attr) => {
          const v = newRoot.getAttribute(attr);
          if (v != null) this.setAttribute(attr, v);
        });
        // Replace inline style (CSS vars) — settings may have changed.
        const newStyle = newRoot.getAttribute('style');
        if (newStyle) this.setAttribute('style', newStyle);
        // Swap children.
        this.innerHTML = newRoot.innerHTML;
      }
      // Refresh refs (panel + backdrop are inside the body which got replaced).
      this.refs.panel = this.querySelector('.pf-cart-drawer__panel');
      this.refs.backdrop = this.querySelector('[data-pf-cart-backdrop]');
      this.refs.body = this.querySelector('[data-pf-cart-body]');
      // Re-bind backdrop click (its element is new).
      this.refs.backdrop?.addEventListener('click', () => this.close(), { signal: this.abortController.signal });
      // Re-bind dynamic listeners on the new line items + close buttons.
      this.bindDynamicListeners();
      // Recommendations container may have changed (different first item,
      // toggled visibility). Re-fetch lazily.
      this.maybeLoadRecommendations();
    }

    syncHeaderBadge(itemCount) {
      // Update any cart count badge in the page (header has data-pf-cart-count).
      document.querySelectorAll('[data-pf-cart-count]').forEach((el) => {
        if (itemCount != null) {
          el.textContent = itemCount;
          el.classList.toggle('is-empty', itemCount === 0);
          el.setAttribute('aria-hidden', itemCount === 0 ? 'true' : 'false');
        }
      });
      // Also sync the drawer's own heading count.
      const headingCount = this.querySelector('[data-pf-cart-heading-count]');
      if (headingCount && itemCount != null) {
        headingCount.textContent = itemCount;
        headingCount.classList.toggle('is-empty', itemCount === 0);
      }
    }

    bumpHeaderBadge() {
      document.querySelectorAll('[data-pf-cart-count]').forEach((el) => {
        el.classList.remove('is-bumping');
        // Force a reflow so the same class can be re-added and the
        // animation re-runs from frame 0.
        void el.offsetHeight;
        el.classList.add('is-bumping');
      });
    }

    // ---- Open / close ----
    open() {
      if (this.classList.contains('is-open')) return;
      this.hidden = false;
      void this.offsetHeight;
      this.classList.add('is-open');
      this.lockScroll();
      // Move focus into the panel for keyboard users.
      const close = this.querySelector('[data-pf-cart-close]');
      setTimeout(() => close && close.focus({ preventScroll: true }), 100);
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.unlockScroll();
      const panel = this.refs.panel;
      const onEnd = () => {
        this.hidden = true;
        panel?.removeEventListener('transitionend', onEnd);
      };
      if (panel) {
        panel.addEventListener('transitionend', onEnd, { once: true, signal: this.abortController.signal });
        setTimeout(onEnd, 340);
      } else {
        this.hidden = true;
      }
    }

    lockScroll() {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }
    unlockScroll() {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }

    // ---- Recommendations ----
    async maybeLoadRecommendations() {
      const recs = this.querySelector('[data-pf-cart-recs]');
      if (!recs) return;

      // Tear down any previous Swiper before we replace / re-init.
      if (this.recsSwiper) {
        try { this.recsSwiper.destroy(true, true); } catch (e) { /* noop */ }
        this.recsSwiper = null;
      }

      // Manual mode: slides are pre-rendered by Liquid from the
      // `recommendations_products` setting. Skip the fetch and just wire
      // up add buttons + Swiper on the existing markup.
      if (recs.hasAttribute('data-pf-cart-recs-manual')) {
        this.bindRecAddButtons(recs);
        this.initRecsSwiper(recs);
        return;
      }

      const productId = recs.dataset.productId;
      const limit = parseInt(recs.dataset.recLimit, 10) || 6;
      const intent = recs.dataset.recIntent || 'related';
      if (!productId) return;

      try {
        const url = new URL('/recommendations/products.json', window.location.origin);
        url.searchParams.set('product_id', productId);
        url.searchParams.set('limit', String(limit));
        url.searchParams.set('intent', intent);
        const res = await fetch(url.toString(), { headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error('Recs fetch failed');
        const data = await res.json();
        const products = Array.isArray(data.products) ? data.products : [];
        if (products.length === 0) {
          recs.hidden = true;
          return;
        }
        this.renderRecommendations(recs, products);
        this.initRecsSwiper(recs);
      } catch (err) {
        // Hide the section if recommendations can't load — don't show an
        // empty carousel.
        recs.hidden = true;
      }
    }

    renderRecommendations(container, products) {
      const list = container.querySelector('[data-pf-cart-recs-list]');
      if (!list) return;

      list.innerHTML = products.map((p) => {
        const variants = Array.isArray(p.variants) ? p.variants : [];
        const isSingleVariant = variants.length <= 1 || (p.options && p.options.length === 1 && p.options[0].toLowerCase() === 'title');
        const firstVariant = variants[0];
        const price = formatMoney(firstVariant?.price);
        const comparePrice = firstVariant?.compare_at_price && parseFloat(firstVariant.compare_at_price) > parseFloat(firstVariant.price)
          ? formatMoney(firstVariant.compare_at_price)
          : '';
        const img = (p.featured_image && (p.featured_image.url || p.featured_image)) || (p.images && p.images[0]) || '';

        let addAttrs;
        if (isSingleVariant && firstVariant && firstVariant.available) {
          addAttrs = `data-pf-cart-rec-add-direct="${firstVariant.id}"`;
        } else if (variants.some((v) => v.available)) {
          addAttrs = `data-pf-cart-rec-add-variants='${escapeJsonAttr(JSON.stringify({
            id: p.id,
            title: p.title,
            featured_image: img,
            options: p.options || [],
            variants: variants,
          }))}'`;
        } else {
          addAttrs = 'disabled';
        }

        return `
          <div class="swiper-slide pf-cart-drawer__rec-card">
            <a href="${escapeHtmlCart(p.url || '#')}" class="pf-cart-drawer__rec-image-link">
              ${img ? `<img class="pf-cart-drawer__rec-image" src="${escapeHtmlCart(typeof img === 'string' ? img : img.url || '')}" alt="${escapeHtmlCart(p.title || '')}" loading="lazy" width="240" height="300">` : `<span class="pf-cart-drawer__rec-image" aria-hidden="true"></span>`}
            </a>
            <div class="pf-cart-drawer__rec-info">
              <h4 class="pf-cart-drawer__rec-title">${escapeHtmlCart(p.title || '')}</h4>
              <div class="pf-cart-drawer__rec-prices">
                ${comparePrice ? `<span class="pf-cart-drawer__rec-price--compare">${comparePrice}</span>` : ''}
                <span class="pf-cart-drawer__rec-price--sale">${price}</span>
              </div>
              <button type="button" class="pf-cart-drawer__rec-add" ${addAttrs}>+ ${escapeHtmlCart('Add')}</button>
            </div>
          </div>
        `;
      }).join('');

      this.bindRecAddButtons(container);
    }

    // Shared between auto (JS-rendered) and manual (Liquid-rendered) cards.
    bindRecAddButtons(container) {
      const list = container.querySelector('[data-pf-cart-recs-list]');
      if (!list) return;

      list.querySelectorAll('[data-pf-cart-rec-add-direct]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const id = parseInt(btn.dataset.pfCartRecAddDirect, 10);
          btn.disabled = true;
          try {
            await this.addToCart(id, 1);
          } finally {
            btn.disabled = false;
          }
        }, { signal: this.abortController.signal });
      });

      list.querySelectorAll('[data-pf-cart-rec-add-variants]').forEach((btn) => {
        btn.addEventListener('click', () => {
          try {
            const product = JSON.parse(btn.dataset.pfCartRecAddVariants);
            this.openVariantModal(product);
          } catch (e) {
            console.warn('[pf-cart] bad variant payload', e);
          }
        }, { signal: this.abortController.signal });
      });
    }

    initRecsSwiper(container) {
      if (!window.Swiper) return;
      const swiperEl = container.querySelector('[data-pf-cart-recs-swiper]');
      const prev = container.querySelector('[data-pf-cart-recs-prev]');
      const next = container.querySelector('[data-pf-cart-recs-next]');
      if (!swiperEl) return;

      const isHorizontal = container.dataset.cardLayout === 'horizontal';
      // Horizontal cards are wide (one per panel with a peek). Vertical
      // cards stack as portraits; show two with a small peek of a third.
      const baseConfig = isHorizontal
        ? { slidesPerView: 1.15, spaceBetween: 10, breakpoints: { 480: { slidesPerView: 1.25, spaceBetween: 12 } } }
        : { slidesPerView: 2.2, spaceBetween: 10, breakpoints: { 480: { slidesPerView: 2.5, spaceBetween: 12 } } };

      this.recsSwiper = new window.Swiper(swiperEl, {
        ...baseConfig,
        watchOverflow: true,
        navigation: prev && next ? { prevEl: prev, nextEl: next } : false,
      });
    }

    // ---- Variant picker modal ----
    openVariantModal(product) {
      const modal = this.querySelector('[data-pf-variant-modal]');
      if (!modal) return;

      this.variantState = {
        product,
        options: product.options || [],
        // Default selection: first variant's options.
        selected: (product.variants[0] && product.variants[0].options ? product.variants[0].options.slice() : []),
      };

      this.renderVariantModal();
      modal.hidden = false;

      const { signal } = this.abortController;
      modal.querySelectorAll('[data-pf-variant-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeVariantModal(), { signal });
      });
      const form = modal.querySelector('[data-pf-variant-form]');
      form?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submitVariant();
      }, { signal });
    }

    closeVariantModal() {
      const modal = this.querySelector('[data-pf-variant-modal]');
      if (modal) modal.hidden = true;
      this.variantState = null;
    }

    renderVariantModal() {
      const modal = this.querySelector('[data-pf-variant-modal]');
      if (!modal || !this.variantState) return;
      const { product, options, selected } = this.variantState;
      const variant = this.findMatchingVariant();

      // Head — image + title + prices.
      const head = modal.querySelector('[data-pf-variant-head]');
      const img = (product.featured_image && (product.featured_image.url || product.featured_image)) || '';
      const price = variant ? formatMoney(variant.price) : '';
      const compare = variant && variant.compare_at_price && parseFloat(variant.compare_at_price) > parseFloat(variant.price)
        ? formatMoney(variant.compare_at_price)
        : '';
      head.innerHTML = `
        ${img ? `<img class="pf-cart-drawer__variant-image" src="${escapeHtmlCart(typeof img === 'string' ? img : img.url || '')}" alt="${escapeHtmlCart(product.title || '')}" width="90" height="90">` : `<span class="pf-cart-drawer__variant-image" aria-hidden="true"></span>`}
        <div>
          <h3 class="pf-cart-drawer__variant-title">${escapeHtmlCart(product.title || '')}</h3>
          <div class="pf-cart-drawer__variant-prices">
            ${compare ? `<span class="pf-cart-drawer__variant-price--compare">${compare}</span>` : ''}
            <span class="pf-cart-drawer__variant-price--sale">${price}</span>
          </div>
        </div>
      `;

      // Options — one swatch group per product option.
      const optionsEl = modal.querySelector('[data-pf-variant-options]');
      optionsEl.innerHTML = options.map((optionName, optIndex) => {
        const values = uniqueValuesAt(product.variants, optIndex);
        const current = selected[optIndex] || '';
        const isColor = /color|colour/i.test(optionName);
        return `
          <fieldset class="pf-cart-drawer__variant-option">
            <legend class="pf-cart-drawer__variant-option-name">
              <span>${escapeHtmlCart(optionName)}:</span>
              <span class="pf-cart-drawer__variant-option-value">${escapeHtmlCart(current)}</span>
            </legend>
            <div class="pf-cart-drawer__variant-swatches">
              ${values.map((value) => {
                // Check if this value, combined with the other already-selected
                // options, maps to an available variant.
                const probe = selected.slice();
                probe[optIndex] = value;
                const match = matchVariant(product.variants, probe);
                const unavailable = !match || !match.available;
                return `
                  <button
                    type="button"
                    class="pf-cart-drawer__variant-swatch${isColor ? ' pf-cart-drawer__variant-swatch--color' : ''}"
                    data-option-index="${optIndex}"
                    data-option-value="${escapeHtmlCart(value)}"
                    aria-pressed="${current === value ? 'true' : 'false'}"
                    data-unavailable="${unavailable ? 'true' : 'false'}"
                    ${isColor ? `style="background-color: ${cssColor(value)};" title="${escapeHtmlCart(value)}"` : ''}
                  >${isColor ? '' : escapeHtmlCart(value)}</button>
                `;
              }).join('')}
            </div>
          </fieldset>
        `;
      }).join('');

      // Bind swatch clicks.
      optionsEl.querySelectorAll('.pf-cart-drawer__variant-swatch').forEach((btn) => {
        btn.addEventListener('click', () => {
          const i = parseInt(btn.dataset.optionIndex, 10);
          const v = btn.dataset.optionValue;
          this.variantState.selected[i] = v;
          this.renderVariantModal();
        }, { signal: this.abortController.signal });
      });

      // Update submit button enabled state. Write into the label span
      // rather than the button itself so the sibling loader markup
      // (data-pf-variant-submit-loader) is preserved across renders.
      const submit = modal.querySelector('[data-pf-variant-submit]');
      if (submit) {
        submit.disabled = !variant || !variant.available;
        const label = submit.querySelector('[data-pf-variant-submit-label]');
        if (label) {
          label.textContent = !variant ? 'Unavailable' : (!variant.available ? 'Sold out' : 'Add to cart');
        }
      }
    }

    findMatchingVariant() {
      if (!this.variantState) return null;
      return matchVariant(this.variantState.product.variants, this.variantState.selected);
    }

    async submitVariant() {
      const variant = this.findMatchingVariant();
      if (!variant || !variant.available) return;
      const submit = this.querySelector('[data-pf-variant-submit]');
      if (submit) {
        submit.classList.add('is-loading');
        submit.disabled = true;
      }
      try {
        await this.addToCart(variant.id, 1);
        this.closeVariantModal();
      } catch (err) {
        // addToCart already logs.
      } finally {
        if (submit) {
          submit.classList.remove('is-loading');
          // Restore disabled state for the error path so the user can
          // retry. On success the modal closes; the next openVariantModal
          // call rebuilds the disabled state from the new variant anyway.
          submit.disabled = !variant.available;
        }
      }
    }
  }

  // ---- helpers ----
  function escapeHtmlCart(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  function escapeJsonAttr(str) {
    // We embed JSON in a single-quoted HTML attribute, so the only chars
    // that can break the attribute are single quotes (and ampersands).
    return String(str).replace(/&/g, '&amp;').replace(/'/g, '&#39;');
  }
  function uniqueValuesAt(variants, index) {
    const seen = new Set();
    const out = [];
    for (const v of variants) {
      const val = (v.options && v.options[index]) || v[`option${index + 1}`];
      if (val == null) continue;
      if (!seen.has(val)) {
        seen.add(val);
        out.push(val);
      }
    }
    return out;
  }
  function matchVariant(variants, selected) {
    return variants.find((v) => {
      const opts = v.options || [v.option1, v.option2, v.option3].filter(Boolean);
      if (opts.length !== selected.length) return false;
      return opts.every((o, i) => o === selected[i]);
    });
  }
  function cssColor(name) {
    // Map common color names to CSS values, falling back to the value
    // itself (which works for "Red", "Olive Green", custom hex strings).
    // CSS handles unknown names by falling back to transparent so the
    // swatch shows the button's background — fine for our default styling.
    if (!name) return 'transparent';
    const lower = String(name).toLowerCase();
    const map = {
      'mild blue': '#a9c7e0',
      'olive green': '#5d6b3f',
      'cream': '#f1ebdd',
    };
    if (map[lower]) return map[lower];
    return name;
  }
  function formatMoney(cents) {
    if (cents == null) return '';
    const code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    const locale = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
      }).format(parseFloat(cents) / 100);
    } catch (e) {
      return '€' + (parseFloat(cents) / 100).toFixed(2);
    }
  }

  customElements.define('pf-cart-drawer', PfCartDrawer);

  // ---------------------------------------------------------------------
  // PfCartPage — full /cart page controller. Shares the same AJAX endpoints
  // and Sections API rendering pattern as the drawer, but operates on its
  // own section markup (left column = line items, right column = summary).
  // Per-line loaders mirror the drawer's behavior; remove buttons set the
  // line to qty=0; discount + note save on submit/blur respectively.
  // ---------------------------------------------------------------------

  if (customElements.get('pf-cart-page')) return;

  class PfCartPage extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.abortController = new AbortController();
      this.bindDynamicListeners();
      this.listenForCartUpdates();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    // Cross-instance sync. See drawer's `dispatchCartUpdated` for context.
    dispatchCartUpdated(data) {
      document.dispatchEvent(new CustomEvent('pf:cart:updated', {
        detail: {
          source: this,
          sourceName: 'page',
          sectionId: this.sectionId,
          sections: (data && data.sections) || null,
          data: data || null,
        },
      }));
    }

    listenForCartUpdates() {
      document.addEventListener('pf:cart:updated', (e) => {
        if (!e.detail || e.detail.source === this) return;
        const active = document.activeElement;
        if (active && this.contains(active)) {
          const tag = active.tagName;
          if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        }
        const html = e.detail.sections && e.detail.sections[this.sectionId];
        if (html) {
          this.replaceSection(html);
          return;
        }
        this.refresh();
      }, { signal: this.abortController.signal });
    }

    async refresh() {
      try {
        // POST to /cart/update.js (no-op body) instead of GET
        // /cart?sections=… — the GET form is unreliable when the page
        // is /cart itself. Updating with empty attributes is a no-op
        // that still returns the requested section HTML.
        const data = await fetchJson('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: {}, sections: this.sectionId }),
        });
        if (data && data.sections && data.sections[this.sectionId]) {
          this.replaceSection(data.sections[this.sectionId]);
        }
      } catch (err) {
        console.warn('[pf-cart-page] refresh failed', err);
      }
    }

    bindDynamicListeners() {
      const { signal } = this.abortController;

      this.querySelectorAll('[data-pf-qty-decrease]').forEach((btn) => {
        btn.addEventListener('click', () => this.changeQty(btn.dataset.lineKey, -1, btn), { signal });
      });
      this.querySelectorAll('[data-pf-qty-increase]').forEach((btn) => {
        btn.addEventListener('click', () => this.changeQty(btn.dataset.lineKey, +1, btn), { signal });
      });
      this.querySelectorAll('[data-pf-qty-input]').forEach((input) => {
        input.addEventListener('change', () => {
          const next = parseInt(input.value, 10);
          if (Number.isNaN(next) || next < 0) {
            input.value = input.defaultValue;
            return;
          }
          this.setQty(input.dataset.lineKey, next, input.closest('[data-pf-cart-line]'));
        }, { signal });
      });

      this.querySelectorAll('[data-pf-cart-remove]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const key = btn.dataset.lineKey;
          const lineEl = btn.closest('[data-pf-cart-line]');
          this.setQty(key, 0, lineEl);
        }, { signal });
      });

      // Discount code submit — same POST shape as the drawer.
      const discountForm = this.querySelector('[data-pf-cart-discount-form]');
      if (discountForm) {
        discountForm.addEventListener('submit', (e) => {
          e.preventDefault();
          const input = discountForm.querySelector('[data-pf-cart-discount-input]');
          const code = (input?.value || '').trim();
          this.applyDiscount(code);
        }, { signal });
      }

      // Notes — debounced save (800ms) + immediate save on blur.
      const noteEl = this.querySelector('[data-pf-cart-note]');
      if (noteEl) {
        let noteTimer = null;
        const queueSave = () => {
          if (noteTimer) clearTimeout(noteTimer);
          noteTimer = setTimeout(() => this.saveNote(noteEl.value), 800);
        };
        noteEl.addEventListener('input', queueSave, { signal });
        noteEl.addEventListener('blur', () => {
          if (noteTimer) clearTimeout(noteTimer);
          this.saveNote(noteEl.value);
        }, { signal });
      }
    }

    async applyDiscount(code) {
      const hint = this.querySelector('[data-pf-cart-discount-hint]');
      try {
        const data = await fetchJson('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            attributes: { discount_code: code },
            sections: getCartSectionIds(),
          }),
        });
        this.applyResponse(data);
      } catch (err) {
        if (hint) {
          hint.hidden = false;
          hint.textContent = err.message || 'Could not save code.';
        }
      }
    }

    async saveNote(value) {
      const status = this.querySelector('[data-pf-cart-note-status]');
      try {
        const data = await fetchJson('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ note: value, sections: getCartSectionIds() }),
        });
        if (status) {
          status.hidden = false;
          clearTimeout(this._noteHideTimer);
          this._noteHideTimer = setTimeout(() => { status.hidden = true; }, 2500);
        }
        // Don't replace own section (textarea cursor would reset); just
        // dispatch so the drawer can pick up the new note value from
        // the payload.
        this.dispatchCartUpdated(data);
      } catch (err) {
        console.warn('[pf-cart-page] save note failed', err);
      }
    }

    async changeQty(key, delta, originBtn) {
      const lineEl = originBtn.closest('[data-pf-cart-line]');
      if (!lineEl) return;
      const input = lineEl.querySelector('[data-pf-qty-input]');
      const current = parseInt(input?.value, 10) || 0;
      const next = Math.max(0, current + delta);
      await this.setQty(key, next, lineEl);
    }

    async setQty(key, quantity, lineEl) {
      if (!lineEl) return;
      lineEl.classList.add('is-loading');
      try {
        const data = await fetchJson('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: getCartSectionIds(),
          }),
        });
        this.applyResponse(data);
      } catch (err) {
        lineEl.classList.remove('is-loading');
        console.warn('[pf-cart-page] change failed', err);
      }
    }

    applyResponse(data) {
      if (data && data.sections && data.sections[this.sectionId]) {
        this.replaceSection(data.sections[this.sectionId]);
      }
      this.syncHeaderBadge(data && typeof data.item_count === 'number' ? data.item_count : null);
      this.dispatchCartUpdated(data);
    }

    replaceSection(html) {
      if (!html) return;
      const temp = document.createElement('div');
      temp.innerHTML = html.trim();
      const newRoot = temp.querySelector('pf-cart-page');
      if (!newRoot) {
        this.innerHTML = temp.innerHTML;
      } else {
        ['data-item-count', 'data-free-shipping-threshold'].forEach((attr) => {
          const v = newRoot.getAttribute(attr);
          if (v != null) this.setAttribute(attr, v);
        });
        const newStyle = newRoot.getAttribute('style');
        if (newStyle) this.setAttribute('style', newStyle);
        this.innerHTML = newRoot.innerHTML;
      }
      this.bindDynamicListeners();
    }

    syncHeaderBadge(itemCount) {
      document.querySelectorAll('[data-pf-cart-count]').forEach((el) => {
        if (itemCount != null) {
          el.textContent = itemCount;
          el.classList.toggle('is-empty', itemCount === 0);
          el.setAttribute('aria-hidden', itemCount === 0 ? 'true' : 'false');
        }
      });
    }
  }

  customElements.define('pf-cart-page', PfCartPage);
})();
